import { useQuery } from "@tanstack/react-query";

import {
  GraphQLAPI,
  GraphQLResult,
  parseSearchResults,
  queries,
} from "@examspark/api";
import { objectiveCompareFn } from "@examspark/common";
import {
  BucketAggregation,
  Mastery,
  ParsedSearchableResourceConnectionNew,
  QuerySearchArgs,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

import { useMutationGetObjective } from "./objectives";

type GetMasteryParams = {
  workspaceIds?: string[];
  classroomId?: string;
  unitId?: string;
};

/** Building this to be more appropriate for workspace-level analytics. */
export const useQueryGetMasteryV2 = (params?: GetMasteryParams) => {
  const getObjective = useMutationGetObjective();
  const authorizationContext = useAuthorizationContext();
  const { workspaceIds } = params ?? {};

  const filter = buildFilter(params);

  return useQuery({
    queryKey: ["objective-mastery", params],
    enabled: !!workspaceIds?.length,
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        filter,
        limit: 0,
        aggsNew: objectiveMasteryAggsByObjectiveId,
      };

      const results = (await GraphQLAPI.query<{
        search: ParsedSearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "GetObjectiveMastery",
          fields: `
            id
            idInc
            idVendor
            alignments {
              items {
                id
                scope
                abbreviation
              }
            }
            resource {
              id
              sk
              idInc
              name
              description
            }
          `,
        }),
        variables,
      })) as GraphQLResult<{ search: ParsedSearchableResourceConnectionNew }>;

      const parsedResult = parseSearchResults(results.data?.search);

      // bucket: alignmentIds
      const buckets = parsedResult.aggregations?.alignmentIds.buckets;
      const _objectiveIds = parsedResult.aggregations?.alignmentIds.buckets.map(
        (obj: BucketAggregation) => ({
          id: obj.key,
        }),
      );

      const objectives = await Promise.all(
        _objectiveIds.map((obj: { id: string }) =>
          getObjective.mutateAsync(obj.id),
        ),
      );

      console.log("parsed", parsedResult);
      // console.log("objectives", _objectiveIds, objectives);
      const _mastery: Mastery[] = buckets.map((obj: any) => ({
        key: obj.key,
        doc_count: obj.doc_count?.value || 0,
        objective: objectives.find((a) => a.id === obj.key),
        scoreMax: obj.maxPoints.value,
        scoreActual: obj.actualPoints.value,
        responseCount: obj.doc_count,
        sessionCount: obj.examSessionCount.value,
        dates: obj.responsesOverTime.buckets.map((w: any) => ({
          key: w.key,
          doc_count: w.doc_count,
          scoreMax: w.maxPoints.value,
          scoreActual: w.actualPoints.value,
        })),
      })) as Mastery[];

      return _mastery.sort((a, b) =>
        objectiveCompareFn(a.objective, b.objective),
      );
    },
  });
};

export const objectiveMasteryAggsByObjectiveId: QuerySearchArgs["aggsNew"] = [
  {
    name: "objectiveDocument",
    type: "terms",
    field: "objectiveDocument",
  },
  {
    name: "alignmentIds",
    type: "terms",
    field: "alignmentIds",
    aggs: [
      {
        name: "examSessionCount",
        type: "cardinality",
        field: "examSessionId",
      },
      {
        name: "maxPoints",
        type: "sum",
        field: "scoreMax",
      },
      {
        name: "actualPoints",
        type: "sum",
        field: "scoreActual",
      },
      {
        name: "responsesOverTime",
        type: "dateHistogram",
        field: "createdAt",
        interval: "1w",
        extendedBounds: {
          min: new Date(
            // 12 weeks ago
            Date.now() - 1000 * 60 * 60 * 24 * 7 * 12,
          ).toISOString(),
          max: new Date().toISOString(),
        },
        aggs: [
          {
            name: "maxPoints",
            type: "sum",
            field: "scoreMax",
          },
          {
            name: "actualPoints",
            type: "sum",
            field: "scoreActual",
          },
        ],
      },
    ],
  },
];

const objectiveMasteryAggsByObjectiveDocumentId: QuerySearchArgs["aggsNew"] = [
  {
    name: "objectiveDocument",
    type: "terms",
    field: "objectiveDocument",
    aggs: [
      {
        name: "examSessionCount",
        type: "cardinality",
        field: "examSessionId",
      },
      {
        name: "maxPoints",
        type: "sum",
        field: "scoreMax",
      },
      {
        name: "actualPoints",
        type: "sum",
        field: "scoreActual",
      },
      {
        name: "responsesOverTime",
        type: "dateHistogram",
        field: "createdAt",
        interval: "1w",
        extendedBounds: {
          min: new Date(
            // 12 weeks ago
            Date.now() - 1000 * 60 * 60 * 24 * 7 * 12,
          ).toISOString(),
          max: new Date().toISOString(),
        },
        aggs: [
          {
            name: "maxPoints",
            type: "sum",
            field: "scoreMax",
          },
          {
            name: "actualPoints",
            type: "sum",
            field: "scoreActual",
          },
        ],
      },
    ],
  },
];

const objectiveMasteryAggsByObjectiveSectionId: QuerySearchArgs["aggsNew"] = [
  {
    name: "objectiveScope",
    type: "terms",
    field: "objectiveScope",
    aggs: [
      {
        name: "examSessionCount",
        type: "cardinality",
        field: "examSessionId",
      },
      {
        name: "maxPoints",
        type: "sum",
        field: "scoreMax",
      },
      {
        name: "actualPoints",
        type: "sum",
        field: "scoreActual",
      },
      {
        name: "responsesOverTime",
        type: "dateHistogram",
        field: "createdAt",
        interval: "1w",
        extendedBounds: {
          min: new Date(
            // 12 weeks ago
            Date.now() - 1000 * 60 * 60 * 24 * 7 * 12,
          ).toISOString(),
          max: new Date().toISOString(),
        },
        aggs: [
          {
            name: "maxPoints",
            type: "sum",
            field: "scoreMax",
          },
          {
            name: "actualPoints",
            type: "sum",
            field: "scoreActual",
          },
        ],
      },
    ],
  },
];

export const buildFilter = (
  params?: GetMasteryParams,
): SearchableResourceFilterInput => {
  const { workspaceIds, classroomId, unitId } = params ?? {};
  const filter: SearchableResourceFilterInput = {
    sk: {
      wildcard: "exam-response*",
    },
    isArchived: {
      ne: true,
    },
    isDeleted: {
      ne: true,
    },
    and: [],
  };

  for (const workspaceId of workspaceIds ?? []) {
    filter.and?.push({
      workspaceId: {
        eq: workspaceId,
      },
    });
  }

  if (classroomId) {
    filter.and?.push({
      tags: {
        eq: `classroom-id#${classroomId}`,
      },
    });
  }

  if (unitId) {
    filter.and?.push({
      tags: {
        eq: `classroom-unit-id#${unitId}`,
      },
    });
  }

  return filter;
};

/** Building this to be more appropriate for workspace-level analytics. */
export const useQueryGetMasteryByObjectiveDocument = (
  params?: GetMasteryParams,
) => {
  const authorizationContext = useAuthorizationContext();
  const { workspaceIds } = params ?? {};

  const filter = buildFilter(params);

  return useQuery({
    queryKey: ["objective-mastery", "by-document", params],
    enabled: !!workspaceIds?.length,
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        filter,
        limit: 0,
        aggsNew: objectiveMasteryAggsByObjectiveDocumentId,
      };

      const results = (await GraphQLAPI.query<{
        search: ParsedSearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "GetObjectiveMastery",
          fields: `
            id
            idInc
            idVendor
            alignments {
              items {
                id
                scope
                abbreviation
              }
            }
            resource {
              id
              sk
              idInc
              name
              description
            }
          `,
        }),
        variables,
      })) as GraphQLResult<{ search: ParsedSearchableResourceConnectionNew }>;

      const parsedResult = parseSearchResults(results.data?.search);
      console.log("parsed", parsedResult);

      const resultsByObjectiveDocument: ObjectiveMasteryByDocument[] =
        parsedResult.aggregations?.objectiveDocument.buckets;

      return resultsByObjectiveDocument;
    },
  });
};

export type ObjectiveMasteryByDocument = {
  key: string;
  doc_count: number;
  actualPoints: ActualPoints;
  responsesOverTime: ResponsesOverTime;
  maxPoints: ActualPoints;
  examSessionCount: ActualPoints;
};

type ResponsesOverTime = {
  buckets: Bucket[];
};

type Bucket = {
  key_as_string: string;
  key: number;
  doc_count: number;
  actualPoints: ActualPoints;
  maxPoints: ActualPoints;
};

type ActualPoints = {
  value: number;
};

/** Building this to be more appropriate for workspace-level analytics. */
export const useQueryGetMasteryByObjectiveSection = (
  params?: GetMasteryParams,
) => {
  const authorizationContext = useAuthorizationContext();
  const { workspaceIds } = params ?? {};

  const filter = buildFilter(params);

  return useQuery({
    queryKey: ["objective-mastery", "by-section", params],
    enabled: !!workspaceIds?.length,
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        filter,
        limit: 0,
        aggsNew: objectiveMasteryAggsByObjectiveSectionId,
      };

      const results = (await GraphQLAPI.query<{
        search: ParsedSearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "GetObjectiveMastery",
          fields: `
            id
            idInc
            idVendor
            alignments {
              items {
                id
                scope
                abbreviation
              }
            }
            resource {
              id
              sk
              idInc
              name
              description
            }
          `,
        }),
        variables,
      })) as GraphQLResult<{ search: ParsedSearchableResourceConnectionNew }>;

      const parsedResult = parseSearchResults(results.data?.search);
      console.log("parsed", parsedResult);

      const resultsByObjectiveScope: ObjectiveMasteryByDocument[] =
        parsedResult.aggregations?.objectiveScope.buckets;

      return resultsByObjectiveScope;
    },
  });
};
